import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const blank = {
  entry_date: new Date().toISOString().slice(0, 10),
  part_code: "",
  part_name: "",
  brand: "",
  product: "",
  model_number: "",
  quantity: 1,
  location: "KTM",
  remarks: "",
};

export default function SparesStockEntry() {
  const [form, setForm] = useState(blank);
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.part_name.trim()) {
      toast.error("Part name is required");
      return;
    }
    if (!form.quantity || Number(form.quantity) <= 0) {
      toast.error("Quantity must be greater than zero");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/spares/stock-entry", {
        ...form,
        quantity: Number(form.quantity),
      });
      toast.success("Stock entry recorded", { description: `Ledger #${data.ledger?.id?.slice(0, 8)}` });
      setRecent(data);
      setForm({ ...blank, entry_date: form.entry_date, location: form.location });
    } catch (err) {
      toast.error("Failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="stock-entry-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Add Part Stock</h1>
        <p className="text-sm text-slate-500 mt-1">
          Manual stock entry — like a purchase entry, but no supplier details required.
        </p>
      </div>

      <Card>
        <CardContent className="p-5">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Fld label="Entry Date" required>
              <Input type="date" value={form.entry_date} onChange={(e) => update("entry_date", e.target.value)} data-testid="entry-date-input" />
            </Fld>
            <Fld label="Part Code">
              <Input value={form.part_code} onChange={(e) => update("part_code", e.target.value)} placeholder="e.g. CMP-X1" data-testid="part-code-input" />
            </Fld>
            <Fld label="Part Name" required>
              <Input value={form.part_name} onChange={(e) => update("part_name", e.target.value)} required data-testid="part-name-input" />
            </Fld>
            <Fld label="Brand">
              <Input value={form.brand} onChange={(e) => update("brand", e.target.value)} data-testid="brand-input" />
            </Fld>
            <Fld label="Product">
              <Input value={form.product} onChange={(e) => update("product", e.target.value)} placeholder="Product / category" data-testid="product-input" />
            </Fld>
            <Fld label="Model Compatibility">
              <Input value={form.model_number} onChange={(e) => update("model_number", e.target.value)} placeholder="model number" data-testid="model-input" />
            </Fld>
            <Fld label="Quantity" required>
              <Input type="number" min={1} value={form.quantity} onChange={(e) => update("quantity", parseInt(e.target.value) || 0)} required data-testid="qty-input" />
            </Fld>
            <Fld label="Location / Warehouse">
              <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm" value={form.location} onChange={(e) => update("location", e.target.value)} data-testid="location-input">
                <option value="KTM">KTM (Kathmandu)</option>
                <option value="BRT">BRT</option>
              </select>
            </Fld>
            <Fld label="Remarks" className="md:col-span-2">
              <Input value={form.remarks} onChange={(e) => update("remarks", e.target.value)} placeholder="optional" data-testid="remarks-input" />
            </Fld>
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button type="submit" disabled={submitting} className="bg-slate-900 text-white" data-testid="submit-stock-entry">
                {submitting ? "Saving..." : "Add to Stock"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {recent && (
        <Card data-testid="stock-entry-result">
          <CardContent className="p-4 text-sm space-y-1">
            <div className="font-heading font-semibold text-slate-900">Last entry saved</div>
            <div className="text-slate-700">
              <span className="font-mono">+{recent.ledger?.change}</span> {recent.ledger?.part_name}
              {recent.ledger?.part_code ? ` (${recent.ledger.part_code})` : ""} · {recent.ledger?.location}
            </div>
            <div className="text-xs text-slate-500">Stock has been increased and a ledger row was added.</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Fld({ label, children, required, className }) {
  return (
    <div className={className}>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
        {label}{required && <span className="text-rose-600 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
